import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Check, ChevronLeft, ChevronRight, Clapperboard, CloudDownload, Loader2, Pause, Play, Settings } from 'lucide-react';
import { RECITERS, clipLabel, photoOf, reciterById } from '@/data/reciters';
import { CHAPTERS, JUZS, chapter } from '@/features/quran/api';
import { firstSurahOfJuz } from './timings';
import { Card, Chip, IconButton, LargeTitle, Section } from '@/components/ui';
import { usePlayer } from './audioEngine';
import { FavoriteButton, ReciterAvatar, ReciterList, type ReciterFilter } from './PlayerUI';
import { useSettings } from '@/lib/store';
import { byName } from '@/data/reciters';
import { ContinueListening } from './ResumeUI';
import { useCanHear } from './access';
import { downloadSurah, isDownloaded, removeDownload } from './downloads';

export function ListenHome() {
  const [filter, setFilter] = useState<ReciterFilter>('all');
  const nav = useNavigate();
  const favs = useSettings((s) => s.settings.favoriteReciters);
  const gender = useSettings((s) => s.settings.gender);
  const can = useCanHear();
  const sister = gender === 'female';
  // Favourites lead the carousel, then the featured reciters, each A–Z.
  const allowed = RECITERS.filter(can);
  const featured = [...allowed.filter((r) => favs.includes(r.id)).sort(byName), ...allowed.filter((r) => r.featured && !favs.includes(r.id)).sort(byName)];
  return (
    <div className="page-pad">
      <LargeTitle title="Listen" ar="التلاوة" subtitle="Reciters & recordings" right={<IconButton label="Settings" to="/settings"><Settings size={18} /></IconButton>} />
      <ContinueListening className="mx-4 mb-4" />
      <div className="px-4 pb-4">
          <Card to="/lectures" className="flex items-center gap-4 p-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-black text-white"><Clapperboard size={22} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-semibold">Lectures</span>
              <span className="block text-[13px] text-muted">Yaqeen, Omar Suleiman, Mufti Menk, Assim al-Hakeem, Belal Assaad, Mohamad Baajour, The Prophet’s Path — in 3-minute parts</span>
            </span>
            <ChevronRight className="shrink-0 text-faint" />
          </Card>
      </div>
      <div className="no-scrollbar flex gap-3 overflow-x-auto px-4 pb-5">
        {featured.map((r) => (
          <Link key={r.id} to={`/listen/${r.id}`} className="press flex w-[132px] shrink-0 flex-col items-center gap-2 rounded-[20px] bg-surface p-3 text-center">
            <ReciterAvatar r={r} size={64} />
            <span className="line-clamp-2 text-[14px] font-semibold leading-tight">{r.name}</span>
            {favs.includes(r.id) ? <span className="text-[12px] text-gold">★ Favourite</span> : r.gender === 'female' && <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-semibold text-gold">Qāriʾah</span>}
          </Link>
        ))}
      </div>
      <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-4">
        <Chip active={filter === 'all'} onClick={() => setFilter('all')}>All reciters</Chip>
        <Chip active={filter === 'favorites'} onClick={() => setFilter('favorites')}>★ Favourites{favs.length ? ` (${favs.length})` : ''}</Chip>
        {sister && <Chip active={filter === 'female'} onClick={() => setFilter('female')}>Women</Chip>}
        {sister && <Chip active={filter === 'male'} onClick={() => setFilter('male')}>Men</Chip>}
      </div>
      {gender == null && <p className="px-5 pb-3 text-[13px] text-muted">Sisters: set your profile (Settings → Profile → “A sister”) to see women reciters.</p>}
      <ReciterList filter={filter} onPick={(id) => nav(`/listen/${id}`)} />
    </div>
  );
}

export function ReciterPage() {
  const { id = '' } = useParams();
  const r = reciterById(id);
  const p = usePlayer();
  const nav = useNavigate();
  const can = useCanHear();
  const [dl, setDl] = useState<Record<number, 'yes' | 'busy' | number>>({});
  const surahs = CHAPTERS.filter((c) => !r.surahs || r.surahs.includes(c.id));
  if (!can(r)) {
    return (
      <div className="page-pad px-6 pt-16 text-center">
        <p className="text-[15px] text-muted">Recordings by women reciters are available to sisters.</p>
        <Link to="/settings/profile" className="mt-3 inline-block font-semibold text-accent">Settings → Profile</Link>
      </div>
    );
  }

  useEffect(() => {
    let alive = true;
    Promise.all(surahs.map(async (c) => [c.id, await isDownloaded(r, c.id)] as const)).then((rows) => {
      if (alive) setDl(Object.fromEntries(rows.filter(([, y]) => y).map(([k]) => [k, 'yes'])));
    });
    return () => { alive = false; };
  }, [r.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const playSurah = (s: number) => {
    if (p.surah === s && p.reciterId === r.id && p.track) { p.toggle(); return; }
    void p.play(s, { reciterId: r.id });
  };

  const download = async (s: number) => {
    setDl((d) => ({ ...d, [s]: 0 }));
    try {
      await downloadSurah(r, s, (pct) => setDl((d) => ({ ...d, [s]: pct })));
      setDl((d) => ({ ...d, [s]: 'yes' }));
    } catch { setDl((d) => { const n = { ...d }; delete n[s]; return n; }); }
  };

  return (
    <div className="page-pad">
      <div className="flex justify-between px-3"><IconButton label="Back" onClick={() => nav(-1)}><ChevronLeft size={20} /></IconButton><FavoriteButton id={r.id} name={r.name} className="bg-fill" /></div>
      <div className="flex flex-col items-center gap-2 px-4 pb-6 text-center">
        <ReciterAvatar r={r} size={96} />
        <h1 className="mt-2 text-[26px] font-bold leading-tight">{r.name}</h1>
        {r.nameAr && <div className="font-arabic text-[20px] text-gold" dir="rtl">{r.nameAr}</div>}
        <div className="text-[14px] text-muted">{[r.country, r.style, r.riwayah].filter(Boolean).join(' · ')}</div>
        {photoOf(r) && <a href={photoOf(r)!.page} target="_blank" rel="noreferrer" className="text-[11px] text-faint">Photo: {photoOf(r)!.artist} · {photoOf(r)!.licence}</a>}
        <button type="button" className="press mt-2 flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 font-semibold text-accent-ink"
          onClick={() => (r.audio === 'juz' ? void p.play(1, { reciterId: r.id, juz: 1 }) : r.audio === 'clips' ? void p.play(r.clips[0].surah, { reciterId: r.id, clip: r.clips[0].id }) : playSurah(surahs[0].id))}>
          <Play size={18} /> Play {r.audio === 'juz' ? 'from Juz 1' : r.audio === 'clips' ? clipLabel(r.clips[0], (s) => CHAPTERS[s - 1].n) : surahs[0].n}
        </button>
      </div>

      {r.licenceStatus === 'needs-permission' && (
        <div className="px-4 pb-4"><Card className="text-[13px] text-muted">This recording is streamed from a public archive. Āyah highlighting and repeat for it will be added once its timings are generated.</Card></div>
      )}

      {r.audio === 'juz' && (
        <Section title="30 ajzāʾ" footer={r.credit}>
          {JUZS.map((j) => {
            const active = p.track?.juz === j.n && p.reciterId === r.id;
            const playing = active && (p.status === 'playing' || p.status === 'gap');
            return (
              <button key={j.n} type="button" className="row press w-full text-left" onClick={() => {
                if (active && p.track) { p.toggle(); return; }
                void p.play(firstSurahOfJuz(j.n), { reciterId: r.id, juz: j.n });
              }}>
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${active ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>
                  {active && p.status === 'loading' ? <Loader2 size={16} className="animate-spin" /> : playing ? <Pause size={16} /> : <Play size={16} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">Juz {j.n}</span>
                  <span className="block truncate text-[13px] text-muted">{chapter(Number(j.first.split(':')[0])).n} {j.first} – {chapter(Number(j.last.split(':')[0])).n} {j.last}</span>
                </span>
              </button>
            );
          })}
        </Section>
      )}

      {r.audio === 'clips' && (
        <Section title={`${r.clips.length} recording${r.clips.length > 1 ? 's' : ''}`} footer={`${r.credit}. Passages are recitals of part of a surah, as performed in tilāwah and tarannum gatherings.`}>
          {r.clips.map((c) => {
            const active = p.track?.clip?.id === c.id && p.reciterId === r.id;
            const playing = active && (p.status === 'playing' || p.status === 'gap');
            return (
              <button key={c.id} type="button" className="row press w-full text-left" onClick={() => {
                if (active && p.track) { p.toggle(); return; }
                void p.play(c.surah, { reciterId: r.id, clip: c.id });
              }}>
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${active ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>
                  {active && p.status === 'loading' ? <Loader2 size={16} className="animate-spin" /> : playing ? <Pause size={16} /> : <Play size={16} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{clipLabel(c, (s) => `${s}. ${CHAPTERS[s - 1].n}`)}</span>
                  <span className="block truncate text-[13px] text-muted">{CHAPTERS[c.surah - 1].t}</span>
                </span>
                <span className="font-arabic text-[17px] text-gold">{CHAPTERS[c.surah - 1].ar}</span>
              </button>
            );
          })}
        </Section>
      )}

      {r.audio !== 'juz' && r.audio !== 'clips' && <Section title={`${surahs.length} surah${surahs.length > 1 ? 's' : ''}`} footer={r.credit}>
        {surahs.map((c) => {
          const active = p.surah === c.id && p.reciterId === r.id;
          const playing = active && (p.status === 'playing' || p.status === 'gap');
          const state = dl[c.id];
          return (
            <div key={c.id} className="row">
              <button type="button" className="press flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => playSurah(c.id)}>
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${active ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>
                  {active && p.status === 'loading' ? <Loader2 size={16} className="animate-spin" /> : playing ? <Pause size={16} /> : <Play size={16} />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{c.id}. {c.n}</span>
                  <span className="block truncate text-[13px] text-muted">{c.t} · {c.v} āyāt</span>
                </span>
              </button>
              {(
                state === 'yes' ? (
                  <button type="button" aria-label="Remove download" className="press grid h-9 w-9 place-items-center rounded-full text-accent" onClick={async () => { if (!confirm(`Remove the ${c.n} download?`)) return; await removeDownload(r, c.id); setDl((d) => { const n = { ...d }; delete n[c.id]; return n; }); }}>
                    <Check size={18} />
                  </button>
                ) : typeof state === 'number' ? (
                  <span className="w-9 text-center text-[12px] tabular-nums text-muted">{state}%</span>
                ) : (
                  <button type="button" aria-label={`Download ${c.n}`} className="press grid h-9 w-9 place-items-center rounded-full text-muted" onClick={() => download(c.id)}><CloudDownload size={18} /></button>
                )
              )}
            </div>
          );
        })}
      </Section>}
    </div>
  );
}
